import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { enableProdMode, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { bootstrapApplication as bootstrapApplication_1, BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { AbstractSecurityStorage, DefaultLocalStorageService, provideAuth } from 'angular-auth-oidc-client';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

import p from '../package.json';

import { AppComponent } from './app/app.component';
import { routing } from './app/app.routing';
import { getAuthConfig } from './app/auth/auth.config';
import { UserService } from './app/core/services/user.service';
import {
    InterventionClosedHandler,
    InterventionGrantedHandler,
    InterventionRefusedHandler,
} from './app/interventions/services/intervention-socket-handlers';
import { NewNotificationHandler } from './app/notifications/services/new-notification-handler';
import { provideFeatureFlags } from './app/shared/feature-flags/feature-flags.providers';
import { AnalyticsGuard } from './app/shared/guards/analytics.guard';
import { AuthGuard } from './app/shared/guards/auth.guard';
import { createTranslateLoader } from './app/shared/i18n/createTranslateLoader';
import { APP_CONFIG, WEBSOCKET, WINDOW_OBJECT } from './app/shared/injectionTokens';
import { TokenInterceptor } from './app/shared/interceptors/TokenInterceptor';
import { AppConfig } from './app/shared/models/AppConfig';
import { AnalyticsService } from './app/shared/services/analytics.service';
import { BobHttpService } from './app/shared/services/bob-http.service';
import { createUiSocket } from './app/shared/services/createUiSocket';
import { HttpService } from './app/shared/services/http.service';
import { LocalStorageService } from './app/shared/services/localStorage.service';
import { ModalService } from './app/shared/services/modal.service';
import { SecurityService } from './app/shared/services/SecurityService';
import { socketActionHandlersToken } from './app/shared/services/SocketActionHandler';
import { UISocketService } from './app/shared/services/ui.socket.service';
import { SharedModule } from './app/shared/shared.module';
import { DeviceCreatedHandler } from './app/site-detail/services/DeviceCreatedHandler';
import { DeviceLabelUpdatedHandler } from './app/site-detail/services/DeviceLabelUpdatedHandler';
import { DeviceRemovedHandler } from './app/site-detail/services/DeviceRemovedHandler';
import { DeviceStatesUpdatedHandler } from './app/site-detail/services/DeviceStatesUpdatedHandler';
import { ExecutionStateChangedHandler } from './app/site-detail/services/ExecutionStateChangedHandler';
import { ServegoAccessGrantedHandler } from './app/site-detail/services/servego-access-granted-handler';
import { DailyVideoCallProvider } from './app/video-conference/services/DailyVideoCallProvider';
import {
    VideoCallHungUpHandler,
    VideoCallInvitationSentHandler,
    VideoCallJoinedParticipantHandler,
    VideoCallLeftParticipantHandler,
} from './app/video-conference/services/video-call-socket-handlers';
import { VideoCallParticipantManager } from './app/video-conference/services/VideoCallParticipantManager';
import { VIDEO_CALL_PROVIDER } from './app/video-conference/services/VideoCallProvider';
import { detectBrowser } from './detectBrowser';
import { environment } from './environments/environment';
import { initializeTagCommander } from './initializeTagCommander';

function bootstrapApplication() {
    fetch(`/config/${environment.configFile}?v=${p.version}`)
        .then((response) => response.json())
        .then((config: AppConfig) => {
            detectBrowser();

            if (environment.production) {
                initializeTagCommander(config);
                enableProdMode();
            }

            bootstrapApplication_1(AppComponent, {
                providers: [
                    {
                        provide: OVERLAY_DEFAULT_CONFIG,
                        useValue: { usePopover: false },
                    },
                    provideZoneChangeDetection(),
                    importProvidersFrom(
                        BrowserModule,
                        FormsModule,
                        ReactiveFormsModule,
                        MatDialogModule,
                        MatExpansionModule,
                        MatGridListModule,
                        routing,
                        SharedModule,
                        MatBadgeModule,
                        MatSelectModule,
                        NgxMaskDirective,
                        ScrollingModule,
                        MatTooltipModule,
                        MatDialogModule,
                    ),
                    {
                        provide: HTTP_INTERCEPTORS,
                        useClass: TokenInterceptor,
                        multi: true,
                    },
                    provideHttpClient(withInterceptorsFromDi()),
                    provideTranslateService({
                        loader: { provide: TranslateLoader, useFactory: createTranslateLoader },
                    }),
                    provideAuth(getAuthConfig(config)),
                    {
                        provide: AbstractSecurityStorage,
                        useClass: DefaultLocalStorageService,
                    },
                    provideAnimations(),
                    provideCharts(withDefaultRegisterables()),
                    provideNgxMask(),
                    { provide: APP_CONFIG, useValue: config },
                    provideFeatureFlags(),
                    AnalyticsService,
                    UserService,
                    ModalService,
                    UISocketService,
                    AuthGuard,
                    AnalyticsGuard,
                    HttpService,
                    BobHttpService,
                    SecurityService,
                    LocalStorageService,
                    {
                        provide: WINDOW_OBJECT,
                        useValue: window,
                    },
                    {
                        provide: WEBSOCKET,
                        useFactory: (config: AppConfig) => createUiSocket(config.servegoApi.url),
                        deps: [APP_CONFIG],
                    },
                    VideoCallParticipantManager,
                    { provide: VIDEO_CALL_PROVIDER, useClass: DailyVideoCallProvider },
                    { provide: 'ValueStorage', useClass: LocalStorageService },
                    { provide: socketActionHandlersToken, useClass: DeviceCreatedHandler, multi: true },
                    { provide: socketActionHandlersToken, useClass: DeviceLabelUpdatedHandler, multi: true },
                    { provide: socketActionHandlersToken, useClass: DeviceRemovedHandler, multi: true },
                    { provide: socketActionHandlersToken, useClass: DeviceStatesUpdatedHandler, multi: true },
                    { provide: socketActionHandlersToken, useClass: ExecutionStateChangedHandler, multi: true },
                    { provide: socketActionHandlersToken, useClass: InterventionClosedHandler, multi: true },
                    { provide: socketActionHandlersToken, useClass: InterventionGrantedHandler, multi: true },
                    { provide: socketActionHandlersToken, useClass: InterventionRefusedHandler, multi: true },
                    { provide: socketActionHandlersToken, useClass: NewNotificationHandler, multi: true },
                    { provide: socketActionHandlersToken, useClass: ServegoAccessGrantedHandler, multi: true },
                    { provide: socketActionHandlersToken, useClass: VideoCallHungUpHandler, multi: true },
                    { provide: socketActionHandlersToken, useClass: VideoCallInvitationSentHandler, multi: true },
                    { provide: socketActionHandlersToken, useClass: VideoCallJoinedParticipantHandler, multi: true },
                    { provide: socketActionHandlersToken, useClass: VideoCallLeftParticipantHandler, multi: true },
                ],
            }).catch((err) => console.error(err));
        });
}

bootstrapApplication();
